<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>

    <div class="sl-card">

        <!-- title -->
        <div class="sl-card__title" :title="prototypeCode || prototypeCodeLegacy">
            {{ prototypeLabel }}
        </div>

        <!-- Parameters Table -->
        <table class="sl-card-table">
            <tbody>

                <!-- Parameters -->
                <tr v-for="prop in article_data" :key="prop.parameterCode">

                    <!-- Parameter label -->
                    <td class="sl-card-table-cell sl-card-table-cell__label">
                        {{ getLabelByParameterCode(prop.parameterCode) }}
                    </td>

                    <!-- Parameter value (not empty) -->
                    <td v-if="prop.parameterValue !== null" class="sl-card-table-cell sl-card-table-cell__value">
                        <span v-html="$pf(prop.parameterValue, prop.parameterCode, 'value')"></span>
                        <span v-if="getUnitByParameterCode(prop.parameterCode)" class="sl-parameter__unit">[&nbsp;{{ getUnitByParameterCode(prop.parameterCode) }}&nbsp;]</span>
                        <span v-if="getHelp(prop.parameterCode, prop.parameterValue)" class="ml-1 text-primary-400 text-xs">
                            ({{ getHelp(prop.parameterCode, prop.parameterValue) }})
                        </span>
                    </td>
                    <!-- Parameter value (empty) -->
                    <td v-else class="sl-card-table-cell sl-card-table-cell__value">
                        -
                    </td>

                </tr>

                <!-- Quantity -->
                <tr>
                    <td class="sl-card-table-cell sl-card-table-cell__label">
                        {{ $t(`parameters.quantity.label`) }}
                    </td>
                    <td class="sl-card-table-cell sl-card-table-cell__value">
                        {{ order.quantity }}
                    </td>
                </tr>

                <!-- Previous Order Reference -->
                <tr v-if="order.previous_order_reference">
                    <td class="sl-card-table-cell sl-card-table-cell__label">
                        {{ $t(`order.previous_order_reference.label`) }}
                    </td>
                    <td class="sl-card-table-cell sl-card-table-cell__value">
                        {{ order.previous_order_reference }}
                    </td>
                </tr>

                <!-- Previous Serial Number -->
                <tr v-if="order.previous_order_reference">
                    <td class="sl-card-table-cell sl-card-table-cell__label">
                        {{ $t(`order.previous_lens_sn.label`) }}
                    </td>
                    <td class="sl-card-table-cell sl-card-table-cell__value">
                        {{ previousSn }}
                    </td>
                </tr>

            </tbody>
        </table>

    </div>

</template>

<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>

import axios from '@/axios';
import { omit } from 'lodash-es';
import ParametersCollection from '@/utils/ParametersCollection.js';

export default
{
    name: 'LensOrderParametersTableCard',

    props:
    {
        order:
        {
            type:     Object,
            required: true,
        },
    },

    data()
    {
        return {
            previousSn: '∅',
        };
    },

    computed:
    {
        article_data()
        {
            // Remove some values
            const data = omit(this.order.article_data, [
                'LENS_SIDE',
                'PROTOTYPE_CODE',
                'PROTOTYPE_CODE_LEGACY',
                'PATIENT_ID',
                'PATIENT_REFERENCE1',
                'PROTOTYPE_VERSION',
            ]);

            const protoCode = this.prototypeCode ?? this.prototypeCodeReference;
            const parametersCollection = new ParametersCollection(this.$store, data, protoCode);

            // Order values
            return parametersCollection.getAccessibleItems();
        },

        prototypeCode()
        {
            return this.order.article_data['PROTOTYPE_CODE'];
        },

        prototypeCodeReference()
        {
            return this.order.article_data['PROTOTYPE_CODE_REFERENCE'];
        },

        prototypeCodeLegacy()
        {
            return this.order.article_data['PROTOTYPE_CODE_LEGACY'];
        },

        prototypeLabel()
        {
            return this.$store.state.prototypes.prototypes.find(p => p.code === this.prototypeCode)?.label
                || this.prototypeCode
                || this.prototypeCodeLegacy;
        },
    },

    created()
    {
        this.fetchPreviousSn();
    },

    methods:
    {
        getLabelByParameterCode(parameterCode)
        {
            return this.$t(`parameters.${parameterCode}.label`);
        },

        getUnitByParameterCode(parameterCode)
        {
            return this.$store.getters['parameters/getAttribute'](parameterCode, 'unit');
        },

        /**
         * Get the help message for a parameter's value,
         * or `null` if there is none.
         *
         * @param {string} code The code of the parameter.
         * @param {*} value     The value of the parameter.
         * @returns {?string}
         */
        getHelp(code, value)
        {
            const tkey = `parameters.${code}.values.${value}.help`;

            if(this.$te(tkey))
            {
                return this.$t(tkey);
            }

            return null;
        },

        fetchPreviousSn()
        {
            if(this.order.previous_order_reference)
            {
                const previousRef = this.order.previous_order_reference;

                axios.get(`/api/order/reference/${previousRef}`)
                    .then(({ data: previousOrder }) =>
                    {
                        this.previousSn = previousOrder.article_data.LENS_SERIAL_NUMBER || '∅';
                    });
            }
        },
    },
};

</script>

<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style scoped>
@reference "#main.css";


.sl-parameter__unit
{
    @apply align-middle ml-1 mr-0.5 text-[8px] text-gray-400;
}

.sl-card-table-cell__label
{

}

.sl-card-table-cell__value
{
    width: 40% !important;
}

</style>
